import React, { useEffect, useRef, useState } from "react";
import useKeypress from "../../../../../hooks/useKeypress";
import useOutsideClick from "../../../../../hooks/useOutsideClick";
import Icon from "../../../Icon";

const getScrollTopFromInsideScroll = (
  outer: Element,
  inner: Element,
  scrollPosition: number
): number => {
  const elHeight = inner.scrollHeight;
  const scrollTop = outer.scrollTop;
  const viewport = scrollTop + outer.clientHeight;
  const elOffset = elHeight * scrollPosition;

  if (elOffset < scrollTop || elOffset + elHeight > viewport) {
    return elOffset;
  }
  return scrollTop;
};

interface SelectProps {
  options?: Array<{ id: any; value: string; hint?: any }>;
  value: any;
  name: string;
  setValue: React.Dispatch<any>;
  placeholder?: string;
  disabled?: boolean;
  customText?: string;
  customTextClassname?: string;
  dropdownButtonClassname?: string;
}

const Select: React.FC<SelectProps> = ({
  options,
  name,
  value,
  setValue,
  placeholder,
  disabled,
  customText,
  customTextClassname,
  dropdownButtonClassname = "text-3xl",
}) => {
  const [query, setQuery] = useState<string>("");
  const [selected, setSelected] = useState<any>(value);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [results, setResults] = useState<
    Array<{ id: any; value: string; hint?: string }>
  >([]);
  const [scrollPosition, setScrollPosition] = useState(() => {
    if (selected != null) {
      return (options || []).findIndex((option) => option.id === selected) || 0;
    }
    return 0;
  });

  const selectRef = useRef<HTMLDivElement | null>(null);
  const inputUpKey = useKeypress("ArrowUp", selectRef, 300);
  const inputDownKey = useKeypress("ArrowDown", selectRef, 300);
  const inputEscapeKey = useKeypress("Escape", selectRef);

  useEffect(() => {
    setResults(
      query !== ""
        ? (options || []).filter((option) =>
            option?.value?.toLowerCase().includes(query.toLowerCase())
          )
        : options || []
    );
  }, [options, query]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (e.target.value === "") {
      setSelected(null);
    }
  };

  useEffect(() => {
    setResults(options || []);
  }, [options]);

  useEffect(() => {
    if (dropdownVisible) {
    } else {
      if (selected != null) {
        setQuery(
          options?.find((option) => option.id === selected)?.value || ""
        );
      } else {
        setQuery("");
        setSelected(null);
      }
      setResults(options || []);
    }
  }, [dropdownVisible, options, selected]);

  useEffect(() => {
    if (selected !== value) {
      setValue(selected);
    }
    setQuery(options?.find((option) => option.id === selected)?.value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selected]);

  useEffect(() => {
    if (value) {
      setSelected(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const toggleDropdown = () => {
    if (!disabled) {
      setDropdownVisible((v) => !v);
    }
  };

  const handleInputEnterKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      !disabled &&
        dropdownVisible &&
        results &&
        results.length &&
        scrollPosition !== -1 &&
        setSelected(results[scrollPosition].id);
      toggleDropdown();
    }
  };

  useEffect(() => {
    !disabled &&
      dropdownVisible &&
      inputUpKey &&
      results &&
      results.length &&
      setScrollPosition((p) =>
        p === -1 ? results.length - 1 : Math.max(p - 1, 0)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownVisible, inputUpKey, results]);

  useEffect(() => {
    !disabled &&
      dropdownVisible &&
      inputDownKey &&
      results &&
      results.length &&
      setScrollPosition((p) => Math.min(p + 1, results.length - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownVisible, inputDownKey, results]);

  useEffect(() => {
    !disabled && dropdownVisible && inputEscapeKey && setDropdownVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownVisible, inputEscapeKey]);

  useOutsideClick(selectRef, () => setDropdownVisible(false));

  useEffect(() => {
    const li = selectRef.current
      ?.getElementsByClassName("select__options")
      .item(scrollPosition);
    const ul = selectRef.current?.querySelector(".__select__");
    if (li && ul) {
      ul.scrollTop = getScrollTopFromInsideScroll(ul, li, scrollPosition);
    }
  }, [scrollPosition]);

  useEffect(() => {
    setScrollPosition(-1);
  }, [results]);

  return (
    <div
      className="relative flex flex-col items-center w-full"
      ref={selectRef}
      onClick={() => !disabled && setDropdownVisible((v) => !v)}
      onKeyPress={handleInputEnterKeyDown}
    >
      <div className="relative w-full z-1">
        {customText ? (
          <p className={customTextClassname && customTextClassname}>
            {customText}
          </p>
        ) : (
          <input
            type="text"
            name={name}
            placeholder={placeholder}
            className="w-full p-3 border rounded-lg focus:outline-none"
            disabled={disabled}
            value={query}
            onChange={handleChange}
            autoComplete="off"
          />
        )}

        <button
          type="button"
          className="absolute top-0 right-0 p-2 cursor-pointer pointer-events-none text-text focus:outline-none"
        >
          <Icon className={dropdownButtonClassname}>keyboard_arrow_down</Icon>
        </button>
      </div>
      {dropdownVisible && (
        <ul
          className="absolute left-0 z-20 w-full p-2 overflow-y-auto bg-white border rounded-b-lg shadow-md __select__ top-full"
          style={{ maxHeight: "20rem" }}
        >
          {results && results.length > 0 ? (
            results.map((option, index) => (
              <li
                key={option.id}
                className={`select__options w-full p-2 cursor-pointer flex flex-col hover:bg-accent rounded-lg ${
                  scrollPosition === index && "bg-accent border"
                }`}
                onClick={setSelected.bind(null, option.id)}
              >
                <span>{option.value}</span>
                {option.hint != null && (
                  <span className="mt-1 text-sm font-medium text-subtitle">
                    {option.hint}
                  </span>
                )}
              </li>
            ))
          ) : (
            <div className="w-full p-2 text-sm font-medium text-center pointer-events-none text-subtitle">
              No options
            </div>
          )}
        </ul>
      )}
    </div>
  );
};

export default Select;
